<script setup>
import {ref, onMounted, defineProps, computed} from 'vue'
import {notify} from '@kyvg/vue3-notification'
import {useUserStore} from '@/stores/UserStore'
import {useDefaultStore} from '@/stores'
import {storeToRefs} from 'pinia'
import {useIsRouteActive} from '@shared/composables/router/useIsRouteActive'
import {PERFORMANCE_REPORT_PLATFORMS} from '@shared/constants/performanceReportPlatforms'
import {accessRules} from '@/permissions/accessControl'
import {authenticationStore} from '@/main'
import SideBarDropdown from '@/components/side-bar/SideBarDropdown.vue'
import SideBarPrimaryDropdown from '@/components/side-bar/SideBarPrimaryDropdown.vue'
import TeamIcon from '@shared-components/ui/icons/side-bar/TeamIcon.vue'
import DashboardIcon from '@shared-components/ui/icons/side-bar/DashboardIcon.vue'
import BalanceBarAlert from '@shared-components/ui/alerts/BalanceBarAlert.vue'
import CreativeStudioIcon from '@shared-components/ui/icons/side-bar/CreativeStudioIcon.vue'

const auth = authenticationStore

const {getUserManager} = useUserStore()
const {totalRequestsCount, meta} = storeToRefs(useDefaultStore())

defineProps({
  fullHeight: {
    type: Boolean,
    default: true,
  },
})

// Refs
const isCollapsed = ref(Number(localStorage.getItem('is-sidebar-collapsed')) || false)
const managerLoading = ref(false)
const hasNoManager = ref(false)
const reportsDropdownRef = ref()
const requestsDropdownRef = ref()
const assetsDropdownRef = ref()
const managersInfo = ref([
  {
    full_name: '',
    position: '',
    photo_path: null,
    manager_type: '',
    email: '',
    skype: '',
    telegram: '',
  },
])

// Computed
const isOpenRequestsInRequestDropdown = computed(() => {
  return Boolean(
    meta.value?.open_requests_counters?.ad_account_request ||
    meta.value?.open_requests_counters?.ad_account_rename_request ||
    meta.value?.open_requests_counters?.ad_account_top_up_request ||
    meta.value?.open_requests_counters?.whitelist_request ||
    meta.value?.open_requests_counters?.share_permissions_request ||
    meta.value?.open_requests_counters?.other_request ||
    false,
  )
})
const creativeStudioDropdown = computed(() => {
  const items = [
    {
      title: 'Our Studio',
      routeName: 'creative-studio-main.our-studio',
      counter: null,
      access: true,
    },
    {
      title: 'Services',
      routeName: 'creative-studio-main.services',
      counter: null,
      access: true,
    },
    {
      title: 'My orders',
      routeName: 'creative-studio-main.orders',
      counter: null,
      access: true,
    },
  ]

  return {
    core: {
      parentRoute: 'creative-studio-main',
    },
    mainButton: {
      title: 'Creative & CRO',
      counter: null,
    },
    items: items.filter(i => i.access),
  }
})
const requestsDropdown = computed(() => {
  const items = [
    {
      title: 'All',
      routeName: 'my-requests.list',
      counter: null,
      access: accessRules.hasAccessToMyRequests(auth),
    },
    {
      title: 'Ad Accounts',
      routeName: 'ad-account-requests.accounts.list',
      counter: meta.value?.open_requests_counters?.ad_account_request ?? null,
      access: accessRules.hasAccessToAdAccountRequests(auth),
    },
    {
      title: 'Rename Ad Account',
      routeName: 'ad-account-requests.rename.list',
      counter: meta.value?.open_requests_counters?.ad_account_rename_request ?? null,
      access: accessRules.hasAccessToAccountRenameRequests(auth),
    },
    {
      title: 'Funds',
      routeName: 'ad-account-requests.top-up.list',
      counter: meta.value?.open_requests_counters?.ad_account_top_up_request ?? null,
      access: accessRules.hasAccessToTopUpRequestsPage(auth),
    },
    {
      title: 'Whitelist Ad Account',
      routeName: 'whitelist',
      counter: meta.value?.open_requests_counters?.whitelist_request ?? null,
      access: accessRules.hasAccessToWhiteListRequestsPage(auth),
    },
    {
      title: 'Share Access',
      routeName: 'share-permissions',
      counter: meta.value?.open_requests_counters?.share_permissions_request ?? null,
      access: accessRules.hasAccessToSharePermissionRequestsPage(auth),
    },
    {
      title: 'FB Pages',
      routeName: 'ad-account-requests.facebook-pages-requests.list',
      counter: null,
      access: accessRules.hasAccessToFBPageRequestsPage(auth),
    },
  ]

  return {
    core: {
      parentRoute: 'ad-account-requests',
    },
    mainButton: {
      title: 'Requests',
      counter: totalRequestsCount.value,
    },
    items: items.filter(item => item.access),
  }
})
const reportsDropdown = computed(() => {
  const items = [
    {
      title: 'Main',
      routeName: 'reports.main',
      counter: null,
      access: accessRules.hasAccessToDashboard(auth),
    },
    {
      title: 'Spend',
      routeName: 'reports.spend',
      counter: null,
      access: accessRules.hasAccessToDashboard(auth),
    },
    {
      title: 'Financial Activity',
      routeName: 'reports.financial-activity',
      counter: null,
      access: accessRules.hasAccessToDashboard(auth),
    },
    ...PERFORMANCE_REPORT_PLATFORMS.map(platform => ({
      title: platform.label,
      routeName: 'reports.performance',
      params: {platform: platform.key},
      counter: null,
      access: accessRules.hasAccessToPerformanceReport(auth, {params: {platform: platform.key}}),
    })),
  ]

  return {
    core: {
      parentRoute: 'client-reports',
    },
    mainButton: {
      title: 'Reports',
      counter: null,
    },
    items: items.filter(i => i.access),
  }
})
const assetsDropdown = computed(() => {
  const items = [
    {
      title: 'Ad Accounts',
      routeName: 'assets.ad-accounts.list',
      counter: null,
      access: true,
    },
  ]

  return {
    core: {
      parentRoute: 'assets.list',
    },
    mainButton: {
      title: 'Ad Accounts & Pages',
      counter: null,
    },
    items: items.filter(i => i.access),
  }
})

// Methods
onMounted(() => {
  getManagerInfo()
})

const getManagerInfo = async () => {
  managerLoading.value = true

  try {
    const managers = await getUserManager()

    if (!managers || managers.length === 0) {
      hasNoManager.value = true
    } else {
      managersInfo.value = managers
    }
  } catch (error) {
    hasNoManager.value = true

    notify({type: 'error', text: error.data.message})
  } finally {
    managerLoading.value = false
  }
}

const showManagerType = manager_type => {
  return manager_type === 'media_planner_social' ? 'Social' : 'Search'
}

const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value
  if (isCollapsed.value) {
    reportsDropdownRef.value?.closeDropdownMenu()
    assetsDropdownRef.value?.closeDropdownMenu()
    requestsDropdownRef.value?.closeDropdownMenu()
  }

  localStorage.setItem('is-sidebar-collapsed', isCollapsed.value ? 1 : 0)
}
</script>

<template>
  <div class="bg-white sidebar position-sticky top-0" :class="{'sidebar--collapsed': isCollapsed}">
    <div class="sidebar-scrollable__inner" :class="{'overflow-y-auto overflow-x-hidden': !isCollapsed}">
      <div class="sidebar-head custom" :class="[isCollapsed ? 'py-9 px-6' : 'pt-9 p-8']">
        <img
          v-if="isCollapsed"
          src="@shared-images/sidebar/pb_logo_mini.png"
          alt="pointboard"
          class="max-w-35-px d-block"
        />
        <img v-else src="@shared-images/sidebar/pb_logo_new.webp" alt="pointboard" class="max-w-170-px d-block" />
      </div>
      <div class="sidebar-body">
        <balance-bar-alert :is-mini="isCollapsed" />

        <ul class="nav nav-pills flex-column" id="menu">
          <li v-if="accessRules.hasAccessToCreativeStudio(auth)">
            <side-bar-primary-dropdown
              ref="creativeStudioDropdownRef"
              id="creative-studio-sidebar-dropdown"
              :data="creativeStudioDropdown"
              :is-mini="isCollapsed"
            >
              <template #button-icon>
                <span class="d-flex align-items-center">
                  <creative-studio-icon />
                </span>
              </template>
            </side-bar-primary-dropdown>
          </li>
          <li v-tooltip.right="isCollapsed ? 'Dashboard' : null">
            <div class="nav-item">
              <router-link
                :class="['nav-link', useIsRouteActive('home').value ? 'active' : '']"
                :to="{name: 'home'}"
                active-class="active"
              >
                <span class="nav-icon d-flex align-items-center">
                  <dashboard-icon />
                </span>
                <span class="nav-text">Dashboard</span>
              </router-link>
            </div>
          </li>
          <li>
            <side-bar-dropdown
              ref="reportsDropdownRef"
              id="reports-sidebar-dropdown"
              parent-route-name="client-reports"
              :data="reportsDropdown"
              :is-mini="isCollapsed"
            >
              <template #button-icon>
                <i class="fa-light fa-chart-line"></i>
              </template>
            </side-bar-dropdown>
          </li>
          <li v-if="accessRules.hasAccessToPolicyPage(auth)" v-tooltip.right="isCollapsed ? 'Ad Policy' : null">
            <div class="nav-item">
              <router-link
                :to="{name: 'policy'}"
                :class="['nav-link', useIsRouteActive('policy').value ? 'active' : '']"
                active-class="active"
              >
                <span class="nav-icon">
                  <i class="fa-light fa-shield-check"></i>
                </span>

                <span class="nav-text">Ad Policy</span>
              </router-link>
            </div>
          </li>
          <li v-if="accessRules.hasAccessToRequestsPage(auth)">
            <side-bar-dropdown
              ref="requestsDropdownRef"
              id="requests-sidebar-dropdown"
              parent-route-name="ad-account-requests"
              :data="requestsDropdown"
              :is-mini="isCollapsed"
              :is-counter="isOpenRequestsInRequestDropdown"
            >
              <template #button-icon>
                <i class="fa-light fa-files"></i>
              </template>
            </side-bar-dropdown>
          </li>
          <li
            v-if="accessRules.hasAccessToCreativeModerationList(auth)"
            v-tooltip.right="isCollapsed ? 'Creative Moderation' : null"
          >
            <div class="nav-item">
              <router-link
                :to="{name: 'creatives.list'}"
                :class="['nav-link justify-content-between', useIsRouteActive('creatives.list').value ? 'active' : '']"
                active-class="active"
              >
                <div class="d-flex align-items-center">
                  <span class="nav-icon">
                    <i class="fa-light fa-image"></i>
                  </span>
                  <span class="nav-text">Creative Moderation</span>
                </div>
                <div v-if="false" class="small-badge-light-border ml-2">Beta</div>
              </router-link>
            </div>
          </li>
          <li v-if="accessRules.hasAccessToAssetsPage(auth)">
            <side-bar-dropdown
              ref="assetsDropdownRef"
              id="ad-accounts-sidebar-dropdown"
              parent-route-name="assets.list"
              :data="assetsDropdown"
              :is-mini="isCollapsed"
            >
              <template #button-icon>
                <i class="fa-light fa-images-user"></i>
              </template>
            </side-bar-dropdown>
          </li>
          <li
            v-if="accessRules.hasAccessToFinancePage(auth)"
            v-tooltip.right="isCollapsed ? 'Financial Overview' : null"
          >
            <div class="nav-item">
              <router-link
                :to="{
                  name: 'payments.daily_balance',
                }"
                :class="['nav-link', useIsRouteActive('finance').value ? 'active' : '']"
                active-class="active"
              >
                <span class="nav-icon">
                  <i class="fa-light fa-coins"></i>
                </span>

                <span class="nav-text">Financial Overview</span>
              </router-link>
            </div>
          </li>
          <li v-if="accessRules.hasAccessToTeam(auth)" v-tooltip.right="isCollapsed ? 'Team' : null">
            <div class="nav-item">
              <router-link
                :to="{name: 'team'}"
                :class="['nav-link', useIsRouteActive('team').value ? 'active' : '']"
                active-class="active"
              >
                <span class="nav-icon">
                  <team-icon />
                </span>

                <span class="nav-text">Team</span>
              </router-link>
            </div>
          </li>
        </ul>

        <div class="pt-3" v-if="!hasNoManager && !isCollapsed">
          <hr class="m-0 pb-3" />
          <div v-for="(manager, index) in managersInfo" :key="index" class="manager-info">
            <div class="px-4 pb-3">
              <p class="fw-bold" v-if="index === 0">I'm here to help you:</p>
              <div class="d-flex gap-3 align-items-center">
                <div v-if="manager.photo_path" class="b-radius-50 w-80-px overflow-hidden border-2-grey">
                  <img :src="manager.photo_path" :alt="manager.full_name" class="d-block max-w-100 w-100" />
                </div>
                <div>
                  <p class="fw-bold mb-0">{{ manager.full_name }} ({{ showManagerType(manager.manager_type) }})</p>
                  <p class="mb-3">{{ manager.position }}</p>
                  <div class="d-flex gap-2 align-items-center">
                    <a
                      v-if="manager.skype"
                      :href="`skype:${manager.skype}?chat`"
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                      class="d-block b-radius-50 h-30-px w-30-px"
                    >
                      <img
                        src="@shared-images/sidebar/socials/teams.webp"
                        alt="skype"
                        class="d-block max-w-100 w-100"
                      />
                    </a>
                    <a
                      v-if="manager.telegram"
                      :href="manager.telegram"
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                      class="d-block b-radius-50 h-30-px w-30-px"
                    >
                      <img
                        src="@shared-images/sidebar/socials/telegram.webp"
                        alt="telegram"
                        class="d-block max-w-100 w-100"
                      />
                    </a>
                    <a
                      v-if="manager.email"
                      :href="`mailto:${manager.email}`"
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                      class="d-block b-radius-50 h-30-px w-30-px"
                    >
                      <img
                        src="@shared-images/sidebar/socials/email.webp"
                        alt="email"
                        class="d-block max-w-100 w-100"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <hr v-if="index + 1 < managersInfo.length" class="mt-0" />
          </div>
        </div>
      </div>
    </div>
    <div class="sidebar__collapse-toggle" @click="toggleSidebar" v-tooltip.right="isCollapsed ? 'Open' : null">
      <img src="@shared-images/sidebar/ui/collapse.svg" alt="collapse" class="d-block" />
      <span v-if="!isCollapsed">Close</span>
    </div>
  </div>
</template>
